<a class="bc-skip-link" href="#main" (click)="focusMain($event)">Skip to main content</a>

<!-- The shell every board shares (docs/multi-org/design/Main.dc.html): the dark rail, the 4px stripe of the
     organisations shown, and the top bar with the filter, "Search or do anything", the bell and you.
     Phone / tablet: docs/redesign/mobile. Desktop (>= 1280) carries neither class. -->
<div class="shell" [class.rail]="isRail" [class.rail-labels]="railLabels" [class.bare]="!loginDisplay || isOnboarding || isChatPage"
     [class.nav-open]="isMobileMenuOpen" [class.phone]="layout.isPhone()" [class.tablet]="layout.useRail()"
     [class.has-tabs]="showTabBar" [class.immersive]="phoneImmersive">

  <!-- ============ Rail ============ -->
  <!-- Not on Notes opened in its own tab: there it works as a chat window.
       Phone: a drawer (a modal dialog while open). Tablet: the rail, or the full rail over the page. -->
  <nav *ngIf="loginDisplay && !isOnboarding && !isChatPage" class="sidebar not-printable" aria-label="Main"
       [attr.role]="overlayNav ? 'dialog' : null" [attr.aria-modal]="overlayNav ? 'true' : null"
       [cdkTrapFocus]="overlayNav">
    <div class="brand-row">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true">B</span>
        <span *ngIf="!isRail" class="brand-text">
          <span class="brand-name">Bizcombo</span>
          <span class="brand-sub">One Workspace</span>
        </span>
        <span *ngIf="isRail" class="bc-sr-only">Bizcombo, One Workspace</span>
      </div>
      <button *ngIf="layout.isPhone()" type="button" class="drawer-close" aria-label="Close menu" (click)="closeMobileNav()">
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
      <!-- Collapsed rail (record pages, tablets): the way back to the full list. Expanded desktops
           collapse it from the avatar menu. -->
      <button *ngIf="!layout.isPhone() && (isRail || overlayNav)" type="button" class="collapse" (click)="toggleRail()"
              [attr.aria-label]="isRail ? 'Expand sidebar' : 'Collapse sidebar'" [attr.aria-expanded]="!isRail">
        <mat-icon aria-hidden="true">{{ isRail ? 'chevron_right' : 'chevron_left' }}</mat-icon>
      </button>
    </div>

    <!-- One list in every module. Each entry is a real link (right-click and middle-click open a new tab
         with ?module= and ?org=); a plain click navigates in this tab. -->
    <ng-container *ngFor="let section of workspaceSections; trackBy: trackSection">
      <div *ngIf="section.heading && !isRail" class="grp" [id]="'bc-nav-h-' + section.key">{{ section.heading }}</div>
      <div *ngIf="section.heading && isRail" class="nav-divider" aria-hidden="true"></div>
      <ul class="modules" [attr.aria-labelledby]="section.heading && !isRail ? 'bc-nav-h-' + section.key : null">
        <li *ngFor="let item of section.items; trackBy: trackItem">
          <a class="nav-link" [href]="item.href" (click)="openItem(item, $event)"
             [attr.target]="item.externalUrl ? '_blank' : null" [attr.rel]="item.externalUrl ? 'noopener' : null"
             [class.active]="item.key === activeKey" [attr.aria-current]="item.key === activeKey ? 'page' : null"
             [attr.aria-label]="isRail ? item.label + (badgeForItem(item) ? ', ' + badgeForItem(item) + ' unread' : '') : null"
             [matTooltip]="isRail ? item.label : ''" matTooltipPosition="right">
            <mat-icon aria-hidden="true">{{ item.icon }}</mat-icon>
            <span *ngIf="railLabels" class="rail-label" aria-hidden="true">{{ item.label }}</span>
            <span *ngIf="!isRail" class="label">{{ item.label }}</span>
            <mat-icon *ngIf="!isRail && item.externalUrl" class="external" aria-hidden="true">open_in_new</mat-icon>
            <span *ngIf="!isRail && badgeForItem(item) as count" class="n">{{ count }}<span class="bc-sr-only"> unread</span></span>
          </a>

          <!-- The active entry's module pages (the module's existing menu), so every page stays reachable. -->
          <ul *ngIf="!isRail && item.key === activeKey && subMenu.length" class="children" [attr.aria-label]="item.label + ' pages'">
            <li *ngFor="let entry of subMenu">
              <a *ngIf="!entry.children" class="child-link" [href]="buildNavHref(entry)" (click)="onNavChildClick(entry, $event)"
                 [class.active]="isSubLinkActive(entry)" [attr.aria-current]="isSubLinkActive(entry) ? 'page' : null">
                {{ entry.name }}
                <span *ngIf="badgeFor(entry) as count" class="n">{{ count }}<span class="bc-sr-only"> need action</span></span>
              </a>
              <ng-container *ngIf="entry.children">
                <button type="button" class="child-group" (click)="toggleSection(entry)" [attr.aria-expanded]="isExpanded(entry)">
                  <span class="label">{{ entry.name }}</span>
                  <mat-icon class="chevron" aria-hidden="true">{{ isExpanded(entry) ? 'expand_less' : 'expand_more' }}</mat-icon>
                </button>
                <ul *ngIf="isExpanded(entry)" class="children children--deep">
                  <li *ngFor="let child of entry.children">
                    <a class="child-link child-link--deep" [href]="buildNavHref(child)" (click)="onNavChildClick(child, $event)"
                       [class.active]="isChildActive(child)" [attr.aria-current]="isChildActive(child) ? 'page' : null">
                      {{ child.name }}
                      <span *ngIf="badgeFor(child) as count" class="n">{{ count }}<span class="bc-sr-only"> need action</span></span>
                    </a>
                  </li>
                </ul>
              </ng-container>
            </li>
          </ul>
        </li>
      </ul>
    </ng-container>

    <!-- Your organizations: shows only that one on My work. A filter, never a switch. -->
    <ng-container *ngIf="orgIdentity.isMultiOrg()">
      <div *ngIf="!isRail" class="grp" id="bc-nav-h-orgs">Your organizations</div>
      <div *ngIf="isRail" class="nav-divider" aria-hidden="true"></div>
      <ul class="modules" [attr.aria-labelledby]="!isRail ? 'bc-nav-h-orgs' : null">
        <li *ngFor="let org of orgIdentity.orgs()">
          <a class="nav-link" [href]="myWorkHref" (click)="showOnlyOrg(org, $event)" [attr.aria-pressed]="isOnlyOrg(org)"
             [attr.aria-label]="'Show only ' + org.name" [matTooltip]="isRail ? org.name : ''" matTooltipPosition="right">
            <bc-org-badge [companyId]="org.id" size="s"></bc-org-badge>
            <span *ngIf="railLabels" class="rail-label" aria-hidden="true">{{ org.initials }}</span>
            <span *ngIf="!isRail" class="label">{{ org.short }}</span>
          </a>
        </li>
      </ul>
    </ng-container>
  </nav>

  <div *ngIf="overlayNav" class="nav-scrim" (click)="closeMobileNav()" aria-hidden="true"></div>

  <!-- ============ Column ============ -->
  <div class="main-col">
    <!-- One band per organisation shown by the top bar's filter. -->
    <div *ngIf="loginDisplay && !isOnboarding && orgIdentity.isMultiOrg()" class="stripe not-printable" aria-hidden="true">
      <span *ngFor="let o of orgLensShown()" [style.background]="o.color"></span>
    </div>
    <header *ngIf="loginDisplay && !isOnboarding" class="topbar not-printable">
      <!-- Phone only: opens the drawer, and the page's title sits beside it. -->
      <button *ngIf="layout.isPhone() && !isChatPage" type="button" class="appbar-btn" (click)="toggleMobileNav($event)"
              aria-haspopup="dialog" [attr.aria-label]="isMobileMenuOpen ? 'Close menu' : 'Open menu'" [attr.aria-expanded]="isMobileMenuOpen">
        <mat-icon aria-hidden="true">{{ isMobileMenuOpen ? 'close' : 'menu' }}</mat-icon>
      </button>
      <div *ngIf="layout.isPhone()" class="appbar-title">
        <span class="appbar-page">{{ pageTitle }}</span>
      </div>

      <!-- 1. Which organisations combined pages show. Filters what you see, never where you save. -->
      <bc-org-lens></bc-org-lens>

      <!-- 2. Search or do anything (Ctrl K): opens the palette below. -->
      <button *ngIf="!layout.isPhone(); else phoneSearch" type="button" class="search" (click)="openPalette()"
              aria-haspopup="dialog" aria-keyshortcuts="Control+K">
        <mat-icon aria-hidden="true">search</mat-icon>
        <span class="search-text">Search or do anything in your organizations</span>
        <kbd aria-hidden="true">Ctrl K</kbd>
      </button>
      <ng-template #phoneSearch>
        <button type="button" class="appbar-btn" aria-label="Search or do anything" aria-haspopup="dialog" (click)="openPalette()">
          <mat-icon aria-hidden="true">search</mat-icon>
        </button>
      </ng-template>

      <!-- 3. The bell with its count. -->
      <app-notification-dropdown
        [notifications]="notifications"
        [unreadCount]="unreadCount"
        [isAdmin]="isAdmin"
        [isLoading]="isLoading"
        [companies]="userCompanies || []"
        [currentCompanyId]="currentCompanyIdFromStorage"
        (markAsRead)="markAsRead($event.note, $event.index)"
        (markAllAsRead)="markAllAsRead()"
        (deleteNotification)="deleteNotification($event.note, $event.index)"
        (viewLeave)="viewPendingLeaves($event)"
        (viewAll)="navigateToNotifications()"
        (openSettings)="navigateToNotificationSettings()">
      </app-notification-dropdown>

      <!-- 4. You: Settings, and the tools that used to sit in the bar. -->
      <span class="av-wrap">
        <button type="button" class="av" [matMenuTriggerFor]="accountMenu" #avatarMenuTrigger="matMenuTrigger"
                [attr.aria-label]="'Your settings' + (userDisplayName ? ', ' + userDisplayName : '')">{{ userInitials }}</button>
        <!-- Who's active opens its own panel here, from the avatar menu or the palette. -->
        <app-team-presence #presenceAnchor class="presence-anchor" aria-hidden="true"></app-team-presence>
      </span>
    </header>

    <main id="main" tabindex="-1">
      <ng-content></ng-content>
    </main>

    <!-- Phone bottom tabs (mockups/phone). "More" opens the drawer. -->
    <bc-tab-bar *ngIf="showTabBar" class="tabbar not-printable" [tabs]="phoneTabs" [moreActive]="moreTabActive"
                [moreExpanded]="isMobileMenuOpen" (more)="toggleMobileNav()"></bc-tab-bar>
  </div>
</div>

<!-- ============ Search or do anything (Search.dc.html) ============ -->
<div *ngIf="paletteOpen" class="palette-wrap not-printable" (click)="closePalette()">
  <section class="palette" #searchContainer role="dialog" aria-modal="true" aria-label="Search or do anything"
           cdkTrapFocus (click)="$event.stopPropagation()">
    <label class="palette-input">
      <mat-icon aria-hidden="true">search</mat-icon>
      <input #searchInput type="text" [formControl]="paletteControl"(keydown)="onPaletteKeydown($event)"
             aria-label="Search or do anything" autocomplete="off" role="combobox" aria-controls="bc-palette-list"
             [attr.aria-expanded]="true" [attr.aria-activedescendant]="'bc-palette-row-' + paletteIndex">
      <mat-spinner *ngIf="isSearching" diameter="18" aria-label="Searching"></mat-spinner>
      <button type="button" class="kbd kbd-btn" (click)="closePalette()" aria-label="Close">Esc</button>
    </label>

    <div class="palette-scope ws-cap">
      <span>Searching in</span>
      <button type="button" class="ws-tag scope-tag" [matMenuTriggerFor]="scopeMenu" aria-label="Change where to search">
        <span class="stack" *ngIf="paletteScopeOrgs.length <= 3 && paletteScopeLabel !== 'All organizations'; else allBadge">
          <bc-org-badge *ngFor="let o of paletteScopeOrgs" [companyId]="o.id" size="s"></bc-org-badge>
        </span>
        <ng-template #allBadge><span class="bd-more" aria-hidden="true">{{ paletteScopeOrgs.length || 1 }}</span></ng-template>
        {{ paletteScopeLabel }}
      </button>
      <ng-container *ngIf="orgIdentity.isMultiOrg()">
        <span>· Try</span><span class="ws-mono">{{ prefixHint }}</span><span>to search one org,</span>
      </ng-container>
      <span *ngIf="!orgIdentity.isMultiOrg()">· Try</span>
      <span class="ws-mono">&gt;</span><span>for actions</span>
      <span *ngFor="let f of searchFilterOptions">
        <span *ngIf="isFilterActive(f.value)" class="ws-tag only-tag">{{ f.label }}</span>
      </span>
    </div>

    <p class="palette-note" *ngIf="!isSearching && unavailableModules.length > 0 && !actionsOnly" role="status">
      <mat-icon aria-hidden="true">cloud_off</mat-icon>
      Some results may be missing: {{ unavailableModules[0].label }} ({{ unavailableModules[0].reason | lowercase }})<span
        *ngIf="unavailableModules.length > 1">, +{{ unavailableModules.length - 1 }} more</span>
    </p>

    <div class="palette-list" id="bc-palette-list" role="listbox" aria-label="Results">
      <div *ngFor="let g of paletteGroups; trackBy: trackGroup" class="palette-group" role="group" [attr.aria-label]="g.label">
        <div class="ws-cap palette-group-head" aria-hidden="true">{{ g.label }}</div>
        <div *ngFor="let row of g.rows; trackBy: trackRow" class="palette-row" role="option" [id]="'bc-palette-row-' + row.index"
             [attr.aria-selected]="row.index === paletteIndex" [class.on]="row.index === paletteIndex"
             (mouseenter)="paletteIndex = row.index!" (click)="runPaletteRow(row, $event)">
          <ng-container *ngIf="row.action as a">
            <span class="act-icon" aria-hidden="true"><mat-icon>{{ a.icon }}</mat-icon></span>
            <span class="row-text"><span class="row-title">{{ a.title }}</span><span class="ws-cap">{{ a.detail }}</span></span>
            <span class="ws-cap" *ngIf="row.index === paletteIndex">Enter</span>
          </ng-container>
          <ng-container *ngIf="row.result as r">
            <bc-org-badge *ngIf="orgIdentity.find(r.companyId); else noBadge" [companyId]="r.companyId" size="m"></bc-org-badge>
            <ng-template #noBadge>
              <span class="act-icon act-icon--neu" aria-hidden="true"><mat-icon>{{ r.restricted ? 'lock' : r.icon }}</mat-icon></span>
            </ng-template>
            <span class="row-text">
              <span class="row-title">{{ r.title }}</span>
              <span class="ws-cap">{{ resultDetail(r) }}</span>
            </span>
            <button *ngIf="canRequestShare(r); else enterHint" type="button" class="share-btn ws-cap ws-warn-t"
                    [attr.aria-disabled]="shareRequestedFormIds.has('' + r.id)"
                    [attr.aria-label]="'Request share of ' + r.title + ' into ' + currentCompanyName"
                    (click)="requestShareFromSearch(r, $event)">
              {{ shareRequestedFormIds.has('' + r.id) ? 'Requested' : 'Request share' }}
            </button>
            <ng-template #enterHint><span class="ws-cap" *ngIf="row.index === paletteIndex">Enter</span></ng-template>
          </ng-container>
        </div>
      </div>
      <p class="palette-empty ws-cap" *ngIf="!isSearching && !paletteGroups.length" role="status">
        No results for “{{ paletteControl.value }}”.
      </p>
    </div>

    <div class="palette-foot ws-cap">
      <span><kbd class="kbd">↑</kbd> <kbd class="kbd">↓</kbd> to move</span>
      <span><kbd class="kbd">Enter</kbd> to open</span>
      <span>Actions that save always name or ask for the org</span>
    </div>
  </section>
</div>

<!-- ============ First sign-in (Welcome.dc.html) ============ -->
<div *ngIf="showWelcome" class="welcome not-printable" role="dialog" aria-modal="true" aria-labelledby="bc-welcome-h" cdkTrapFocus>
  <div class="stripe" aria-hidden="true"><span *ngFor="let o of orgIdentity.orgs()" [style.background]="o.color"></span></div>
  <div class="welcome-bar"><span class="brand-mark" aria-hidden="true">B</span><span class="welcome-brand">Bizcombo</span></div>
  <div class="welcome-main">
    <div class="welcome-body">
      <div class="welcome-head">
        <span class="ws-cap welcome-step">Step 1 of 2 · Your organizations</span>
        <h1 id="bc-welcome-h" class="welcome-h1">Welcome{{ currentUser?.firstName ? ', ' + currentUser.firstName : '' }}. You work in {{ orgIdentity.orgs().length }} organizations.</h1>
        <p class="welcome-lead">You'll see all of them together, with no switching. Each one has its own colour and initials, so you can always tell whose record you're looking at.</p>
      </div>

      <section class="ws-card" aria-label="Your organizations">
        <div class="ws-tbl"><div class="welcome-tbl" [class.wide]="welcomeHasAccessColumns">
          <div class="ws-th" [style.grid-template-columns]="welcomeHasAccessColumns ? 'minmax(0, 1fr) 120px 200px 90px' : 'minmax(0, 1fr) 90px'"><span>Organization</span><ng-container *ngIf="welcomeHasAccessColumns"><span>Your role</span><span>Modules</span></ng-container><span style="text-align: right">Favourite</span></div>
          <div *ngFor="let o of orgIdentity.orgs()" class="ws-tr" [style.grid-template-columns]="welcomeHasAccessColumns ? 'minmax(0, 1fr) 120px 200px 90px' : 'minmax(0, 1fr) 90px'">
            <span class="ws-oc">
              <bc-org-badge [companyId]="o.id" size="l"></bc-org-badge>
              <span class="welcome-org"><span class="ws-ell" style="font-weight: 600">{{ o.name }}</span>
                <span class="ws-cap" *ngIf="isMasterCompany(o)">Your master company</span></span>
            </span>
            <ng-container *ngIf="welcomeHasAccessColumns">
              <span><span class="ws-chip" *ngIf="roleIn(o) as role" [ngClass]="role === 'Viewer' ? 'warn' : 'info'">{{ role }}</span></span>
              <span class="ws-cap" style="font-size: 13px">{{ modulesIn(o) }}</span>
            </ng-container>
            <span style="text-align: right">
              <button type="button" class="star" [class.on]="favourites.has(o.id)" [attr.aria-pressed]="favourites.has(o.id)"
                      [attr.aria-label]="'Favourite ' + o.name" (click)="toggleFavourite(o)">
                <mat-icon aria-hidden="true">{{ favourites.has(o.id) ? 'star' : 'star_outline' }}</mat-icon>
              </button>
            </span>
          </div>
        </div></div>
      </section>

      <section class="ws-card ws-pad welcome-views">
        <span class="ws-cap welcome-step">Step 2 of 2 · What should pages show first?</span>
        <div class="views">
          <button *ngFor="let v of startViews" type="button" class="ws-pick view" [class.on]="startView === v.id"
                  [attr.aria-pressed]="startView === v.id" (click)="startView = v.id">
            <span style="font-weight: 600">{{ v.label }}</span><span class="ws-cap">{{ startViewHint(v.id) }}</span>
          </button>
        </div>
        <div *ngIf="masterCompanyName" class="ws-callout info">
          <mat-icon aria-hidden="true">info</mat-icon>
          <span>Your timesheets and leave are kept by your master company, <b>{{ masterCompanyName }}</b>. You can log time for clients in any organization.</span>
        </div>
      </section>

      <div class="welcome-foot">
        <span class="ws-cap">You can change all of this later from the menu under your initials.</span>
        <button type="button" class="ws-btn ws-btn-p welcome-go" (click)="finishWelcome()">
          Open my workspace<mat-icon aria-hidden="true">arrow_forward</mat-icon>
        </button>
      </div>
    </div>
  </div>
</div>

<!-- ============ Menus (overlays) ============ -->

<!-- Searching in: organisations and record types. -->
<mat-menu #scopeMenu="matMenu" class="bc-shell-menu search-companies-menu">
  <button mat-menu-item (click)="allPaletteOrgs(); $event.stopPropagation()">
    <mat-icon>{{ paletteScopeLabel === 'All organizations' ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
    <span>All organizations</span>
  </button>
  <button mat-menu-item *ngFor="let o of orgIdentity.orgs()" (click)="togglePaletteOrg(o.id); $event.stopPropagation()">
    <mat-icon>{{ paletteOrgOn(o.id) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
    <bc-org-badge [companyId]="o.id" size="s" withName></bc-org-badge>
  </button>
  <mat-divider></mat-divider>
  <button mat-menu-item *ngFor="let f of searchFilterOptions" (click)="toggleSearchFilter(f.value); $event.stopPropagation()">
    <mat-icon>{{ isFilterActive(f.value) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
    <span>Only {{ f.label | lowercase }}</span>
  </button>
</mat-menu>

<!-- Organisations: the old rail picker, same actions (view as client, settings, open in a new tab). -->
<mat-menu #companyMenu="matMenu" class="company-picker-menu bc-shell-menu">
  <div class="company-picker-filter" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
    <label class="bc-sr-only" for="bc-org-filter">Find an organisation</label>
    <input id="bc-org-filter" class="company-picker-filter-input" #companyFilter placeholder="Find an organisation"
           type="text" (keyup)="onCompanySearch(companyFilter.value)">
  </div>
  <div class="company-picker-list">
    <div *ngFor="let company of filteredCompanies" class="company-picker-row"
         [class.current]="company.id === currentCompanyIdFromStorage" (click)="$event.stopPropagation()">
      <bc-org-badge [companyId]="company.id" size="s"></bc-org-badge>
      <span class="company-picker-row-label" [class.clickable]="isAdmin"
            [matTooltip]="isAdmin ? 'Open this company in Organisation settings' : ''"
            (click)="openCompanySettingsInPlace(company.id, $event, avatarTrigger)">{{ company.name }}</span>
      <span class="company-picker-row-actions" *ngIf="company.id !== currentCompanyIdFromStorage">
        <button mat-icon-button [attr.aria-label]="'View ' + company.name + ' as a client in a new tab'"
                matTooltip="View as client (new tab)" (click)="openCompanyAsForm(company.id, $event)">
          <mat-icon>open_in_new</mat-icon>
        </button>
        <button *ngIf="isAdmin" mat-icon-button [attr.aria-label]="'Organisation settings for ' + company.name + ' in a new tab'"
                matTooltip="Organisation settings (new tab)" (click)="openCompanySettings(company.id, $event)">
          <mat-icon>settings</mat-icon>
        </button>
        <button mat-icon-button [attr.aria-label]="'Open ' + company.name + ' in a new tab'"
                matTooltip="Switch to this company (new tab)" (click)="switchToCompany(company.id, $event)">
          <mat-icon>swap_horiz</mat-icon>
        </button>
      </span>
      <mat-icon *ngIf="company.id === currentCompanyIdFromStorage" class="company-picker-current-icon" aria-label="Current organisation">check</mat-icon>
    </div>
  </div>
</mat-menu>

<!-- Company settings for this tab's organisation (admins only). -->
<mat-menu #orgSettingsMenu="matMenu" class="dash-layout-menu bc-shell-menu">
  <div class="dash-layout-head" (click)="$event.stopPropagation()">
    <span class="dash-layout-title">Company settings</span>
    <span class="dash-layout-count">Opens in a new tab</span>
  </div>
  <button mat-menu-item *ngFor="let section of companySettingsSections" (click)="openCurrentCompanySettings(section.step)">
    <mat-icon>{{ section.icon }}</mat-icon>
    <span>{{ section.label }}</span>
  </button>
  <button mat-menu-item class="dash-layout-reset" (click)="openCurrentCompanySettings()">
    <mat-icon>open_in_new</mat-icon>
    <span>All settings</span>
  </button>
</mat-menu>

<!-- Feedback: report a bug or request a feature (Ctrl+Shift+B opens these in the palette). -->
<mat-menu #feedbackMenu="matMenu" class="bc-shell-menu">
  <button mat-menu-item (click)="startFeedback(TicketType.Bug)"><mat-icon>bug_report</mat-icon><span>Report a bug</span></button>
  <button mat-menu-item (click)="startFeedback(TicketType.Feature)"><mat-icon>lightbulb</mat-icon><span>Request a feature</span></button>
</mat-menu>

<!-- You: Settings first, then the tools that left the top bar. -->
<mat-menu #accountMenu="matMenu" xPosition="before" class="bc-shell-menu">
  <div class="account-head" *ngIf="currentUser" (click)="$event.stopPropagation()">
    <span class="account-name">{{ userDisplayName }}</span>
    <span class="account-email">{{ currentUser.email }}</span>
    <!-- Check in / check out for the day (renders nothing while attendance is switched off). -->
    <app-attendance-check-in class="account-checkin"></app-attendance-check-in>
  </div>
  <a mat-menu-item routerLink="/settings/user"><mat-icon>settings</mat-icon><span>Settings</span></a>
  <a mat-menu-item routerLink="/settings/notifications"><mat-icon>notifications</mat-icon><span>Notification settings</span></a>
  <a mat-menu-item routerLink="/settings/organization"><mat-icon>corporate_fare</mat-icon><span>Organisation settings</span></a>
  <button mat-menu-item *ngIf="canOpenCompanySettings && currentCompanyName" [matMenuTriggerFor]="orgSettingsMenu">
    <mat-icon>tune</mat-icon><span>Company settings: {{ currentCompanyName }}</span>
  </button>
  <a mat-menu-item *ngIf="canAdministerPermissions" routerLink="/settings/permissions">
    <mat-icon>admin_panel_settings</mat-icon><span>User permissions</span>
  </a>
  <ng-container *ngxPermissionsOnly="['CorporateAdmin']">
    <a mat-menu-item routerLink="/corporate/risk-settings"><mat-icon>assessment</mat-icon><span>Risk assessment</span></a>
    <a mat-menu-item routerLink="/admin/pending-changes"><mat-icon>rule</mat-icon><span>Pending changes</span></a>
  </ng-container>
  <a mat-menu-item routerLink="/drafts"><mat-icon>edit_note</mat-icon><span>My drafts</span></a>
  <mat-divider></mat-divider>
  <button mat-menu-item [matMenuTriggerFor]="companyMenu" (menuOpened)="onCompanyMenuOpened()">
    <mat-icon>business</mat-icon><span>Organisations</span>
  </button>
  <button mat-menu-item *ngIf="orgIdentity.isMultiOrg()" (click)="openWelcome()">
    <mat-icon>star_outline</mat-icon><span>Favourites and start view</span>
  </button>
  <button mat-menu-item *ngIf="createItems.length" (click)="openPalette('>New ')">
    <mat-icon>add</mat-icon><span>New…</span>
  </button>
  <button mat-menu-item (click)="openPresence()"><mat-icon>groups</mat-icon><span>Who's active</span></button>
  <button mat-menu-item data-page-finder-toggle (click)="pageFinder.toggle()">
    <mat-icon>manage_search</mat-icon><span>Find on this page</span>
  </button>
  <a mat-menu-item [href]="phonebookUrl" (click)="onContactsClick($event)">
    <mat-icon>contacts</mat-icon><span>Contacts</span>
  </a>
  <button mat-menu-item (click)="toggleOutlookPanel()">
    <mat-icon>mail_outline</mat-icon><span>Messages (Outlook)</span>
  </button>
  <button mat-menu-item [matMenuTriggerFor]="feedbackMenu"><mat-icon>bug_report</mat-icon><span>Feedback</span></button>
  <button mat-menu-item *ngIf="canCollapseRail" (click)="toggleRail()">
    <mat-icon>{{ isRail ? 'chevron_right' : 'chevron_left' }}</mat-icon><span>{{ isRail ? 'Expand sidebar' : 'Collapse sidebar' }}</span>
  </button>
  <mat-divider></mat-divider>
  <button mat-menu-item (click)="onLogout()"><mat-icon>logout</mat-icon><span>Log out</span></button>
</mat-menu>
